/**
 * Shared types and helpers for the platform-admin screens.
 *
 * Every handler behind these calls is `platform.*` gated and reads its subject
 * tenant from a route param, never from the caller's own tenant.
 */

import { SubscriptionStatus } from '@/lib/shared-types';

/** The Prisma enum, re-exported rather than redeclared. */
export { SubscriptionStatus };

export const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = Object.values(SubscriptionStatus);

export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceMonthly: string;
  currency: string;
  maxBranches: number | null;
  maxUsers: number | null;
  maxDevices: number | null;
  /** Module enablement, e.g. `{ inventory: true }`. Operational, not editorial. */
  features: Record<string, unknown> | null;
  /**
   * Bilingual copy for the plan's card on tarabeeza.com. Null until written,
   * and a plan without it is left off the public pricing band rather than drawn
   * with an empty body — see `Pages/Plans/marketing.ts`.
   */
  marketing: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: string;
  _count?: { subscriptions: number };
}

export interface TenantRef {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export interface Subscription {
  id: string;
  tenantId: string;
  planId: string;
  status: SubscriptionStatus;
  startsAt: string;
  endsAt: string | null;
  trialEndsAt: string | null;
  plan?: Plan;
  tenant?: TenantRef;
}

export interface PlatformOverview {
  totals: {
    tenants: number;
    branches: number;
    users: number;
    devices: number;
    liveSubscriptions: number;
  };
  subscriptionsByStatus: Partial<Record<SubscriptionStatus, number>>;
}

export interface TenantStats {
  tenant: TenantRef & { createdAt: string };
  counts: { branches: number; users: number; devices: number; activeSubscriptions: number };
  subscription: Subscription | null;
}

export interface FeatureFlag {
  id: string;
  key: string;
  description: string | null;
  enabled: boolean;
  /** Per-tenant exceptions live in one JSON column keyed by tenant id. */
  overrides: Record<string, boolean> | null;
  createdAt: string;
  updatedAt: string;
}

export interface Device {
  id: string;
  branchId: string | null;
  userId: string | null;
  type: string;
  name: string;
  /**
   * Null until the device pairs. The server blanks it deliberately: an unpaired
   * row stores `pending-pair:<hmac>:<expiry>` in this column, and printing that
   * would put the HMAC of a live pairing code on a support screen.
   *
   * It was typed `string` here — a hand-written copy of a server shape that had
   * moved on — so TypeScript was satisfied while `.slice()` threw on every
   * venue with a device waiting to be paired.
   */
  fingerprint: string | null;
  /** Why there is no fingerprint, which `isActive: false` alone cannot say. */
  pairing?: { status: 'PAIRED' | 'PENDING' | 'EXPIRED'; expiresAt: string | null };
  isActive: boolean;
  lastSeenAt: string | null;
  branch?: { id: string; name: string; code: string } | null;
  user?: { id: string; firstName: string | null; lastName: string | null } | null;
}

/** Surface the API's own message rather than a generic failure line. */
export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(' · ');
  return message ?? fallback;
}

/** `null` on a plan ceiling means unlimited, not zero. */
export function limitLabel(value: number | null, unlimited: string): string {
  return value == null ? unlimited : String(value);
}
